import type { Skier } from '../physics/skier'
import type { Race } from '../race/race'
import { formatDelta, formatTime } from '../race/race'
import { SNOW_TYPES, SNOW_TYPE_COUNT } from '../terrain/snow'
import { clamp01 } from '../core/math'

/**
 * Race HUD, plus the debug panel behind it.
 *
 * The clock is the score now, so the timing readout is not debug UI — it is the
 * game's only feedback loop and has to be readable at speed on a phone. It sits
 * top-centre, big, with the delta to the line you are chasing directly under
 * it, colour-coded so you can read it without focusing on it.
 *
 * The old debug panel stays behind `H`, because spec §4's warning still applies:
 * you cannot tune feel without seeing what the surface is doing.
 */

export interface HudStats {
  fps: number
  ticks: number
  slopeName: string
  slopeBlurb: string
  difficulty: string
  conditions: string
  vertical: number
  bestTime: number | null
  /** Downhill position of each ghost, for the live gap readout. */
  bestZ: number | null
  lastResult: { time: number; improved: boolean } | null
  sluffLoad: number
  colorMode: string
  drawCalls: number
  triangles: number
  /** Render tier and the pixel ratio it actually got. */
  quality: string
}

export class Hud {
  private readonly race: HTMLDivElement
  private readonly panel: HTMLDivElement
  private readonly toggle: HTMLButtonElement
  private acc = 0
  private visible: boolean

  constructor(parent: HTMLElement) {
    this.race = document.createElement('div')
    this.race.className = 'race'
    parent.appendChild(this.race)

    this.panel = document.createElement('div')
    this.panel.className = 'hud'
    parent.appendChild(this.panel)

    this.toggle = document.createElement('button')
    this.toggle.className = 'hud-toggle'
    this.toggle.textContent = '◍'
    this.toggle.title = 'Toggle debug overlay (H)'
    this.toggle.addEventListener('pointerdown', (e) => {
      e.stopPropagation()
      this.setVisible(!this.visible)
    })
    parent.appendChild(this.toggle)

    this.visible = !matchMedia('(pointer: coarse)').matches
    this.setVisible(this.visible)
  }

  setVisible(v: boolean): void {
    this.visible = v
    this.panel.style.display = v ? 'block' : 'none'
  }

  isVisible(): boolean {
    return this.visible
  }

  update(skier: Skier, race: Race, stats: HudStats, dt: number): void {
    this.acc += dt
    if (this.acc < 1 / 20) return
    this.acc = 0

    this.renderRace(skier, race, stats)
    if (this.visible) this.renderPanel(skier, race, stats)
  }

  // -- The race readout ----------------------------------------------------

  private renderRace(skier: Skier, race: Race, stats: HudStats): void {
    // Gap to a ghost is measured in *distance* while running, because a time
    // gap is undefined until you both reach the same point. Metres ahead or
    // behind is what you can actually see out of the window anyway.
    const gap = (ghostZ: number | null): string => {
      if (ghostZ == null || race.state !== 'running') return '—'
      const d = skier.z - ghostZ
      const cls = d >= 0 ? 'ahead' : 'behind'
      return `<b class="${cls}">${d >= 0 ? '+' : ''}${d.toFixed(0)}m</b>`
    }

    const splitRow = race.splits
      .map((s, i) => `<span class="split ${s.time != null ? 'done' : ''}">S${i + 1} ${s.time != null ? s.time.toFixed(2) : '·'}</span>`)
      .join('')

    let banner = ''
    if (race.state === 'ready') {
      banner = `<div class="race-banner">drop in to start</div>`
    } else if (race.finished && stats.lastResult) {
      // Arcade: the comparison is your own best, nothing else.
      const vsBest = stats.bestTime != null && !stats.lastResult.improved
        ? stats.lastResult.time - stats.bestTime : null
      banner = `<div class="race-banner finish">
        <b>${stats.lastResult.improved ? 'PERSONAL BEST' : 'FINISH'}</b>
        <span>${formatTime(stats.lastResult.time)}</span>
        ${vsBest != null ? `<span class="behind">${formatDelta(vsBest)} vs best</span>` : ''}
        <span class="hud-dim">R to run again</span>
      </div>`
    }

    this.race.innerHTML = `
      <div class="race-clock ${race.finished ? 'stopped' : ''}">${formatTime(race.time)}</div>
      <div class="race-gaps">
        <span class="hud-dim">best</span> ${gap(stats.bestZ)}
      </div>
      <div class="race-splits">${splitRow}</div>
      ${banner}`
  }

  // -- Debug panel ---------------------------------------------------------

  private renderPanel(skier: Skier, race: Race, stats: HudStats): void {
    let bestK = 0
    for (let k = 1; k < SNOW_TYPE_COUNT; k++) {
      if (skier.snow.weights[k] > skier.snow.weights[bestK]) bestK = k
    }

    this.panel.innerHTML = `
      <div class="hud-title">FALL LINE <span class="hud-dim">chase your best</span></div>
      <div class="hud-dim hud-face"><b class="d-${stats.difficulty}">${stats.slopeName}</b> · ${Math.round(stats.vertical)}m vert</div>
      <div class="hud-dim hud-face">${stats.slopeBlurb}</div>
      <div class="hud-dim hud-face">${stats.conditions}</div>

      ${section('times')}
      ${row('your best', formatTime(stats.bestTime))}
      ${row('current', formatTime(race.time))}

      ${section('run')}
      ${row('state', `<b class="s-${skier.state}">${skier.state.toUpperCase()}</b>${skier.crashReason !== 'none' ? ` <span class="hud-dim">${skier.crashReason}</span>` : ''}`)}
      ${row('speed', `${skier.speed.toFixed(1)} m/s <span class="hud-dim">${(skier.speed * 3.6).toFixed(0)} km/h</span>`)}
      ${bar('', clamp01(skier.speed / 35), speedColor(skier.speed))}
      ${row('slope', `<b style="color:${slopeColor(skier.slopeDeg)}">${skier.slopeDeg.toFixed(0)}°</b>`)}
      ${row('crashes', String(skier.crashes))}

      ${section('surface')}
      ${row('snow', `<b>${skier.snow.name}</b> <span class="hud-dim">${(skier.snow.weights[bestK] * 100).toFixed(0)}%</span>`)}
      ${snowBars(skier)}
      ${row('resistance', `${skier.snow.resistance.toFixed(3)} <span class="hud-dim">plough</span>`)}
      ${row('grip used', `<b style="color:${skier.gripUsed > 1 ? '#ff6b5e' : '#7fdc8a'}">${(skier.gripUsed * 100).toFixed(0)}%</b>`)}
      ${bar('', clamp01(skier.gripUsed), skier.gripUsed > 1 ? '#ff6b5e' : '#7fdc8a')}
      ${row('sluff', `${(stats.sluffLoad * 100).toFixed(0)}%`)}

      ${section('perf')}
      ${row('fps', `<b style="color:${stats.fps < 50 ? '#ffb454' : '#7fdc8a'}">${stats.fps.toFixed(0)}</b> <span class="hud-dim">${stats.ticks} tick/f</span>`)}
      ${row('draws', `${stats.drawCalls} <span class="hud-dim">${(stats.triangles / 1000).toFixed(0)}k tris</span>`)}
      ${row('shading', stats.colorMode)}
      ${row('quality', stats.quality)}

      <div class="hud-keys">
        drag steer · hold tuck · swipe↓ check · swipe↑ pop<br>
        <span class="hud-dim">A/D steer · SPACE tuck · S check · W pop · R restart · [ ] slope · G shading · H hud · P pause</span>
      </div>`
  }
}

function snowBars(skier: Skier): string {
  let out = ''
  for (let k = 0; k < SNOW_TYPE_COUNT; k++) {
    const w = skier.snow.weights[k]
    if (w < 0.02) continue
    out += bar(SNOW_TYPES[k].name, w, SNOW_BAR_COLORS[k])
  }
  return out
}

const SNOW_BAR_COLORS = ['#e8f2ff', '#9fb3c8', '#c8b98f', '#7fd4e8']

function section(label: string): string {
  return `<div class="hud-section">${label}</div>`
}

function row(label: string, value: string): string {
  return `<div class="hud-row"><span class="hud-label">${label}</span><span class="hud-value">${value}</span></div>`
}

function bar(label: string, t: number, color: string): string {
  const pct = (clamp01(t) * 100).toFixed(1)
  return `<div class="hud-bar-wrap">${label ? `<span class="hud-label hud-barlabel">${label}</span>` : ''}<div class="hud-bar"><i style="width:${pct}%;background:${color}"></i></div></div>`
}

/** Spec §3 inspection bands: green <30, yellow 30-38, orange 38-45, red 45+. */
function slopeColor(deg: number): string {
  if (deg < 30) return '#6fd08a'
  if (deg < 38) return '#e8d34a'
  if (deg < 45) return '#ef9436'
  return '#ff5f52'
}

function speedColor(v: number): string {
  if (v < 12) return '#6fd08a'
  if (v < 24) return '#e8d34a'
  return '#ef9436'
}
